import React, { useState, useEffect, useRef } from 'react';
import { Listbox } from '@headlessui/react';
import { Button } from '../catalyst/button';
import { Input } from '../catalyst/input';
import { Text } from '../catalyst/text';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../catalyst/dialog';
import { useTaskService } from '../../hooks/useTaskService';
import useOtmsService from '../../hooks/useOtmsService';
import { taskStructureTemplates } from '../../data/taskStructureTemplates';
import { 
  XMarkIcon, 
  PlusIcon, 
  ChevronUpDownIcon, 
  PencilIcon, 
  ClipboardDocumentIcon,
  ListBulletIcon,
  CalendarIcon,
  TableCellsIcon,
  ChartBarIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Single Select' },
  { value: 'multi-user', label: 'Multiple Users' },
  { value: 'multi-organization', label: 'Multiple Organization Units' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'checklist', label: 'Checklist', defaultItems: ['Item 1', 'Item 2', 'Item 3'] }
];

const DEFAULT_WORKFLOW_STEPS = [
  'Not Started',
  'In Progress',
  'Under Review',
  'Completed'
];

const DEFAULT_ROLES = [
  'Owner',
  'Assignee',
  'Reviewer',
  'Observer'
];

// List of system-specific chat-based types to filter out
const CHAT_BASED_TYPES = ['Chat', 'Message', 'Conversation', 'Discussion'];

const TaskAddModal = ({ isOpen, onClose, onTaskCreated }) => {
  const { taskType, tasks } = useTaskService();
  const { personnel } = useOtmsService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [frequentTypes, setFrequentTypes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);
  
  // Form states
  const [taskForm, setTaskForm] = useState({
    name: '',
    priority: 'Medium',
    hasParent: false
  });

  // Initialize with task types from database
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);

        // Load task types from API
        const response = await taskType.getAll();
        
        // Filter out chat-based types
        const filteredTemplates = (response || [])
          .filter(template => !CHAT_BASED_TYPES.some(chatType => 
            template.name.toLowerCase().includes(chatType.toLowerCase())
          ))
          .map(template => ({
            id: template.id,
            name: template.name,
            description: template.description,
            structures: template.structures || [],
            category: 'Task Types',
            isPredefined: false
          }));

        setTaskTypes(filteredTemplates);

        // Load frequently used types
        // In a real implementation, this would come from an API call or local storage
        // For now, we'll simulate it with the first 6 non-chat templates
        await loadFrequentTypes(filteredTemplates);

        // Clear any selected template when reloading
        setSelectedTemplate(null);
      } catch (error) {
        console.error('Error loading task types:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  // Load frequently used types
  const loadFrequentTypes = async (allTypes) => {
    try {
      // In a real implementation, this would be an API call to get usage statistics
      // For now, we'll simulate it with localStorage or the first 6 templates
      
      // Try to get from localStorage first
      const storedFrequentTypes = localStorage.getItem('frequentTaskTypes');
      let frequentTypeIds = [];
      
      if (storedFrequentTypes) {
        try {
          frequentTypeIds = JSON.parse(storedFrequentTypes);
        } catch (e) {
          console.error('Error parsing stored frequent types:', e);
        }
      }
      
      // If we have stored IDs, find those templates
      let frequentTemplates = [];
      if (frequentTypeIds.length > 0) {
        frequentTemplates = allTypes.filter(t => frequentTypeIds.includes(t.id));
      }
      
      // If we don't have enough, add some defaults
      if (frequentTemplates.length < 6) {
        const remainingCount = 6 - frequentTemplates.length;
        const existingIds = frequentTemplates.map(t => t.id);
        const additionalTemplates = allTypes
          .filter(t => !existingIds.includes(t.id))
          .slice(0, remainingCount);
        
        frequentTemplates = [...frequentTemplates, ...additionalTemplates];
      }
      
      setFrequentTypes(frequentTemplates.slice(0, 6));
    } catch (error) {
      console.error('Error loading frequent types:', error);
      // Fallback to first 6 templates
      setFrequentTypes(allTypes.slice(0, 6));
    }
  };

  // Update frequent types when a template is selected
  const updateFrequentTypes = (selectedId) => {
    try {
      // Get current frequent types from localStorage
      const storedFrequentTypes = localStorage.getItem('frequentTaskTypes');
      let frequentTypeIds = [];
      
      if (storedFrequentTypes) {
        try {
          frequentTypeIds = JSON.parse(storedFrequentTypes);
        } catch (e) {
          console.error('Error parsing stored frequent types:', e);
        }
      }
      
      // Remove the selected ID if it exists
      const filteredIds = frequentTypeIds.filter(id => id !== selectedId);
      
      // Add the selected ID to the front
      const updatedIds = [selectedId, ...filteredIds].slice(0, 10); // Keep top 10
      
      // Save back to localStorage
      localStorage.setItem('frequentTaskTypes', JSON.stringify(updatedIds));
    } catch (error) {
      console.error('Error updating frequent types:', error);
    }
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setSearchQuery(template.name);
    setIsDropdownOpen(false);
    setShowSearch(false);
    
    // Update frequent types
    updateFrequentTypes(template.id);
  };

  const initializeFilledData = (template) => {
    const definition = template.structures?.[0]?.definition;
    if (!definition) {
      throw new Error('No valid structure definition found in template');
    }

    const filledData = {
      fields: {},
      workflowSteps: (definition.workflowSteps || []).map((step, index) => ({
        name: step,
        order: index,
        isComplete: false,
        completedAt: null,
        completedBy: null,
        startedAt: index === 0 ? new Date().toISOString() : null
      })),
      roles: (definition.roles || []).map((role, index) => ({
        name: role,
        order: index,
        users: []
      }))
    };

    // Initialize only custom fields (not root fields)
    definition.fields?.forEach(field => {
      // Skip if it's a root field
      if (['name', 'description', 'effort', 'timeSpent', 'completionPercentage', 'startedAt', 'dueDate', 'completedAt'].includes(field.name)) {
        return;
      }

      switch (field.type) {
        case 'checklist':
          filledData.fields[field.name] = {
            type: 'checklist',
            label: field.label,
            value: field.items?.map(item => ({
              text: item,
              checked: false,
              checkedAt: null,
              checkedBy: null
            })) || []
          };
          break;

        case 'multi-user':
        case 'multi-organization':
          filledData.fields[field.name] = {
            type: field.type,
            label: field.label,
            value: []
          };
          break;

        case 'select':
          filledData.fields[field.name] = {
            type: 'select',
            label: field.label,
            value: '',
            options: field.options || []
          };
          break;

        case 'boolean':
          filledData.fields[field.name] = {
            type: 'boolean',
            label: field.label,
            value: false
          };
          break;

        case 'number':
          filledData.fields[field.name] = {
            type: 'number',
            label: field.label,
            value: 0
          };
          break;

        case 'currency':
          filledData.fields[field.name] = {
            type: 'currency',
            label: field.label,
            value: 0,
            currencySymbol: field.currencySymbol || '$'
          };
          break;

        case 'date':
          filledData.fields[field.name] = {
            type: 'date',
            label: field.label,
            value: null
          };
          break;

        default:
          filledData.fields[field.name] = {
            type: field.type || 'text',
            label: field.label,
            value: ''
          };
      }
    });

    return filledData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }

    try {
      setError(null);
      const filledData = initializeFilledData(selectedTemplate);
      
      // Get the structure ID from the selected template or use the template ID directly
      const structureId = selectedTemplate.structures?.[0]?.id || selectedTemplate.id;
      if (!structureId) {
        throw new Error('No valid structure ID found');
      }
      
      const taskData = {
        name: taskForm.name,
        description: taskForm.description,
        priority: 'Medium',
        status: filledData.workflowSteps[0]?.name || 'Not Started',
        hasParent: false,
        taskTypeStructureId: structureId,
        filledData
      };

      const response = await tasks.create(taskData);
      if (response?.data) {
        if (onTaskCreated) {
          await onTaskCreated(response.data);
        }
        onClose();
        // Reset form state
        setTaskForm({
          name: '',
          description: '',
          priority: 'Medium',
          hasParent: false
        });
        setSelectedTemplate(null);
        setSearchQuery('');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      setError(error.message || 'Error creating task');
    }
  };

  const filteredTaskTypes = taskTypes.reduce((acc, template) => {
    if (!acc[template.category]) acc[template.category] = [];
    
    // Check if template matches search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = template.name.toLowerCase().includes(searchLower);
      const descriptionMatch = template.description?.toLowerCase().includes(searchLower);
      if (nameMatch || descriptionMatch) {
        acc[template.category].push(template);
      }
    } else {
      // When no search query, still add the template to its category
      acc[template.category].push(template);
    }
    return acc;
  }, {});

  // Filter out empty categories and limit results when no search query
  const nonEmptyCategories = Object.entries(filteredTaskTypes)
    .filter(([_, templates]) => templates.length > 0)
    .map(([category, templates]) => {
      // If no search query, limit to 5 templates per category
      if (!searchQuery && templates.length > 5) {
        return [category, templates.slice(0, 5)];
      }
      return [category, templates];
    });

  // Get a template icon based on name
  const getTemplateIcon = (name) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('list') || nameLower.includes('todo')) {
      return <ListBulletIcon className="h-6 w-6 text-zinc-100" />;
    } else if (nameLower.includes('calendar') || nameLower.includes('schedule')) {
      return <CalendarIcon className="h-6 w-6 text-zinc-100" />;
    } else if (nameLower.includes('table') || nameLower.includes('spreadsheet')) {
      return <TableCellsIcon className="h-6 w-6 text-zinc-100" />;
    } else if (nameLower.includes('chart') || nameLower.includes('report')) {
      return <ChartBarIcon className="h-6 w-6 text-zinc-100" />;
    } else if (nameLower.includes('document') || nameLower.includes('note')) {
      return <DocumentTextIcon className="h-6 w-6 text-zinc-100" />;
    } else {
      return <ClipboardDocumentIcon className="h-6 w-6 text-zinc-100" />;
    }
  };

  // Get a background color based on template name
  const getTemplateColor = (name, index) => {
    const colors = [
      'bg-zinc-700',
      'bg-zinc-700',
      'bg-zinc-700',
      'bg-zinc-700',
      'bg-zinc-700',
      'bg-zinc-700'
    ];
    
    return colors[index % colors.length];
  };

  const renderTaskForm = () => (
    <div className="space-y-4">
      {!selectedTemplate && !showSearch ? (
        <div>
          <div className="flex justify-between items-center mb-4">
            <Text variant="title-sm">Task Types</Text>
            <Button 
              variant="ghost" 
              size="xs" 
              onClick={() => setShowSearch(true)}
              className="flex items-center gap-1 text-zinc-500"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Search All Types
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {frequentTypes.map((template, index) => (
              <div 
                key={template.id}
                className="border border-zinc-200 rounded-lg cursor-pointer hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="flex items-start p-4">
                  <div className={`flex-none rounded-lg p-3 mr-3 ${getTemplateColor(template.name, index)}`}>
                    {getTemplateIcon(template.name)}
                  </div>
                  <div>
                    <Text variant="body-sm" className="font-medium text-zinc-900">
                      {template.name} →
                    </Text>
                    <Text variant="xs" className="text-zinc-500 mt-1 line-clamp-2">
                      {template.description || 'Create a new task with this template.'}
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {error && (
            <div className="mt-4 p-2 bg-red-50 text-red-700 rounded text-xs">
              {error}
            </div>
          )}
        </div>
      ) : showSearch && !selectedTemplate ? (
        <div>
          <div className="flex justify-between items-center mb-4">
            <Text variant="title-sm">Search Task Types</Text>
            <Button 
              variant="ghost" 
              size="xs" 
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
              }}
              className="text-zinc-500"
            >
              Back to Frequent Types
            </Button>
          </div>
          
          <div className="relative w-full mb-4" ref={searchRef}>
            <Input
              size="sm"
              type="text"
              placeholder="Search for task types..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              className="w-full"
              leadingIcon={<MagnifyingGlassIcon className="h-4 w-4 text-zinc-400" />}
            />
            {isDropdownOpen && (
              <div className="absolute mt-1 w-full z-10 bg-white rounded-md shadow-lg ring-1 ring-zinc-200 max-h-64 overflow-auto task-type-dropdown">
                {nonEmptyCategories.length > 0 ? (
                  <>
                    {nonEmptyCategories.map(([category, templates]) => (
                      <div key={category}>
                        <div className="px-3 py-1 text-xs font-semibold text-zinc-500 bg-zinc-50">
                          {category}
                        </div>
                        {templates.map((template) => (
                          <div
                            key={template.id}
                            className="px-3 py-1.5 cursor-pointer hover:bg-zinc-50"
                            onClick={() => handleTemplateSelect(template)}
                          >
                            <Text variant="body-xs" className="font-medium text-zinc-900">
                              {template.name}
                            </Text>
                            {template.description && (
                              <Text variant="xs" className="text-zinc-500">
                                {template.description}
                              </Text>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-zinc-500">
                    No templates found
                  </div>
                )}
              </div>
            )}
          </div>
          
          {!searchQuery && (
            <div className="grid grid-cols-1 gap-2">
              {taskTypes.map((template) => (
                <div
                  key={template.id}
                  className="px-3 py-2 border border-zinc-200 rounded cursor-pointer hover:bg-zinc-50"
                  onClick={() => handleTemplateSelect(template)}
                >
                  <Text variant="body-sm" className="font-medium text-zinc-900">
                    {template.name}
                  </Text>
                  {template.description && (
                    <Text variant="xs" className="text-zinc-500">
                      {template.description}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {error && (
            <div className="mt-4 p-2 bg-red-50 text-red-700 rounded text-xs">
              {error}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          <div>
            <div className="flex justify-between items-center mb-2">
              <Text variant="body-sm" className="font-medium text-zinc-900">Selected Type: {selectedTemplate?.name}</Text>
              <Button 
                variant="ghost" 
                size="xs" 
                onClick={() => {
                  setSelectedTemplate(null);
                  setSearchQuery('');
                }}
                className="text-zinc-500"
              >
                Change Type
              </Button>
            </div>
            <Input
              size="xs"
              label="Task Name"
              placeholder="Enter a descriptive name for the task"
              value={taskForm.name}
              onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            
            {error && (
              <div className="mt-4 p-2 bg-red-50 text-red-700 rounded text-xs">
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="2xl"
    >
      <DialogTitle>
        {selectedTemplate ? `Create New ${selectedTemplate.name}` : 'Create New Task'}
      </DialogTitle>

      <DialogBody>
        {renderTaskForm()}
      </DialogBody>

      <DialogActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {selectedTemplate && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || !taskForm.name || !selectedTemplate}
          >
            Create Task
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TaskAddModal;